/**
 * Thin wrapper around posthog-js.
 *
 * This is the ONLY module in the app that imports posthog-js. Everything else
 * calls track() / identifyUser() from here, which keeps the whole integration
 * removable: delete this file, `hooks/useFormTelemetry.ts` and
 * `App/AnalyticsProvider.tsx`, drop the three `useFormTelemetry` calls, and the
 * app is back to where it started.
 *
 * Analytics is OFF unless REACT_APP_POSTHOG_KEY is set. When it is unset every
 * export below is a no-op and posthog-js is never even downloaded, so
 * .env.local / .env.demo / .env.uat builds stay completely untouched.
 *
 * Privacy posture (see docs/analytics.md for the full rationale):
 *   - autocapture is limited to <button> and <a> clicks, so table rows
 *     containing shipper / consignee / AWB text are never captured
 *   - session recording is disabled
 *   - users are identified by Firebase uid + role only, never name or email
 *   - URLs are collapsed to route patterns, so Firestore document ids stay local
 *   - form telemetry records field NAMES only, never field VALUES
 */

// posthog-js is loaded dynamically, so these are type-only imports.
import type { PostHog } from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

/**
 * Runtime kill switch, for when you want to use the app without generating
 * events (testing an unrelated feature, demoing, debugging a form).
 *
 *   localStorage.setItem('hyperway_analytics', 'off')   // then reload
 *   localStorage.removeItem('hyperway_analytics')       // then reload
 *
 * Checked before posthog-js is imported, so when muted the library is never
 * even downloaded. Persists across reloads, needs no rebuild, and works in
 * production builds as well as local dev.
 */
const MUTE_KEY = 'hyperway_analytics';

const isMuted = (): boolean => {
  try {
    return window.localStorage.getItem(MUTE_KEY) === 'off';
  } catch (e) {
    // Private browsing / storage disabled — fail open, analytics stays on.
    return false;
  }
};

/**
 * Which environment produced the event: 'local', 'UAT', 'CHL-PROD', ...
 *
 * PostHog's free plan only allows ONE project, so local/UAT testing lands in
 * the same project as production. This is attached to every event as a super
 * property so you can filter with `environment = CHL-PROD` in any insight.
 */
const ENVIRONMENT = process.env.REACT_APP_FIREBASE_ENV_DEV_CODENAME || 'unknown';

/** Shape of the object posthog hands to before_send. Declared locally so we
 *  don't depend on posthog-js internals. */
type CaptureLike = {
  event: string;
  properties?: { [key: string]: any };
} | null;

let client: PostHog | null = null;
let loadPromise: Promise<PostHog | null> | null = null;

/** Route patterns registered by AnalyticsProvider, used to collapse URLs. */
let routePatterns: string[] = [];

/** True only when a key is configured AND the runtime kill switch is off. */
export const analyticsEnabled = (): boolean => Boolean(POSTHOG_KEY) && !isMuted();

// ---------------------------------------------------------------------------
// URL / text sanitisation
// ---------------------------------------------------------------------------

/** Matches AWB-shaped strings, e.g. "160-12345678" or "16012345678". */
const AWB_PATTERN = /\b\d{3}-?\d{8}\b/g;
/** Matches anything email-shaped. */
const EMAIL_PATTERN = /\b[\w.+-]+@[\w-]+\.[\w.]+\b/g;

export const setRoutePatterns = (patterns: string[]): void => {
  routePatterns = patterns;
};

const patternToRegExp = (pattern: string): RegExp => new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+').replace(/\//g, '\\/') + '$');

/**
 * Collapse a concrete pathname to the route pattern that produced it, so
 * `/operator/order/aB3xK9pQ` is reported as `/operator/order/:id`. Keeps the
 * analytics aggregatable AND keeps document ids out of PostHog.
 */
export const normalizePath = (pathname: string): string => {
  for (let i = 0; i < routePatterns.length; i++) {
    if (patternToRegExp(routePatterns[i]).test(pathname)) return routePatterns[i];
  }
  // Unknown route: scrub any segment that looks like an id rather than leak it.
  return pathname
    .split('/')
    .map((segment) => (/^[A-Za-z0-9_-]{12,}$/.test(segment) || /^\d+$/.test(segment) ? ':id' : segment))
    .join('/');
};

/** Strip query string and hash, then collapse the path to its route pattern. */
const sanitizeUrl = (value: unknown): unknown => {
  if (typeof value !== 'string' || !value) return value;
  try {
    const url = new URL(value, window.location.origin);
    return url.origin + normalizePath(url.pathname);
  } catch (e) {
    return value;
  }
};

const scrubText = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  return value.replace(AWB_PATTERN, '[awb]').replace(EMAIL_PATTERN, '[email]').slice(0, 120);
};

/**
 * Last line of defence before anything leaves the browser. Runs on EVERY event,
 * including autocapture events we did not write by hand.
 */
const sanitizeEvent = (payload: CaptureLike): CaptureLike => {
  if (!payload || !payload.properties) return payload;
  const props = payload.properties;

  const urlKeys = ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer'];
  urlKeys.forEach((key) => {
    if (props[key]) props[key] = sanitizeUrl(props[key]);
  });

  if (props.$pathname) props.$pathname = normalizePath(String(props.$pathname));

  // Autocapture element text: allowlisted to buttons/links, but a button label
  // can still interpolate customer data, so scrub it anyway.
  if (props.$el_text) props.$el_text = scrubText(props.$el_text);
  if (Array.isArray(props.$elements)) {
    props.$elements.forEach((el: any) => {
      if (el && el.$el_text) el.$el_text = scrubText(el.$el_text);
    });
  }

  return payload;
};

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * Dynamically import and initialise posthog-js. The dynamic import keeps
 * posthog-js in its own chunk so it never lands in the main bundle.
 */
export const initAnalytics = (): Promise<PostHog | null> => {
  if (!POSTHOG_KEY || isMuted()) return Promise.resolve(null);
  if (client) return Promise.resolve(client);
  if (loadPromise) return loadPromise;

  loadPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,

        // Only buttons and links, and only clicks. This is what stops a click
        // on an order table row from shipping the row's text (shipper name,
        // consignee, AWB number) to PostHog.
        autocapture: {
          dom_event_allowlist: ['click'],
          element_allowlist: ['button', 'a'],
        },
        mask_all_element_attributes: true,

        // Pageviews are sent by hand from AnalyticsProvider so they carry the
        // normalised route pattern rather than the raw URL.
        capture_pageview: false,
        capture_pageleave: true,

        // Replay records the DOM, which on the MAWB/HAWB forms means real
        // shipper and consignee data. Off deliberately.
        disable_session_recording: true,

        before_send: sanitizeEvent as any,
      });

      // Stamped on every event so local/UAT noise can be filtered out of
      // production insights (free plan = a single shared project).
      posthog.register({ environment: ENVIRONMENT });

      // Local debugging only: lets you run `posthog.debug()` in the browser
      // console to watch events as they are sent. Stripped from prod builds.
      if (process.env.NODE_ENV === 'development') {
        (window as any).posthog = posthog;
      }

      client = posthog;
      return posthog;
    })
    .catch((error) => {
      // Analytics must never break the app.
      console.warn('[analytics] failed to load', error);
      return null;
    });

  return loadPromise;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const track = (event: string, properties?: { [key: string]: any }): void => {
  if (!analyticsEnabled()) return;
  initAnalytics().then((posthog) => {
    if (posthog) posthog.capture(event, properties);
  });
};

/**
 * Identify by Firebase uid and role only. Deliberately no name and no email:
 * roles give you all the segmentation you need, and the uid can be joined back
 * to Firestore internally if you ever genuinely need the person.
 */
export const identifyUser = (uid: string, traits: { roles?: string[]; isAdmin?: boolean; isReadOnlyRole?: boolean }): void => {
  if (!analyticsEnabled() || !uid) return;
  initAnalytics().then((posthog) => {
    if (!posthog) return;
    posthog.identify(uid, {
      roles: traits.roles || [],
      isAdmin: Boolean(traits.isAdmin),
      isReadOnlyRole: Boolean(traits.isReadOnlyRole),
    });
  });
};

/** Call on logout so the next user on a shared machine is a separate person. */
export const resetAnalytics = (): void => {
  if (!POSTHOG_KEY || !client) return;
  client.reset();
};

export const capturePageview = (pathname: string): void => {
  track('$pageview', { $current_url: window.location.origin + normalizePath(pathname) });
};
